import React, { useEffect, useRef } from 'react';
import { formatCalendarDate } from '../../utils/calendarDate';
import { track, EVENTS } from '../../services/analytics';
import { AD_LANDING_ATTR } from '../../hooks/useAdLanding';

// The paid first screen of a college page (#4476, founder pick A of the
// ad-landing mocks, 2026-10-16): a one-line name row, the ad's question and
// its answer, the "Where do I stand" card, the tiles, then the photo and the
// paragraph.
//
// No ad copy is markup on the page (#4492, with the college pages' Google
// drop): the answers this school can give ride a non-rendered
// <script type="application/json">, and the block is built from it only on a
// paid visit. index.html's head script marks <html data-ad-landing> before
// the page paints; the small script right after the empty slot (the same
// adLandingHtml the app uses, serialized) fills the slot while the page is
// still being parsed, before anything below it is, so a paid visitor's first
// paint is already this layout. A crawler or an organic visitor gets an empty
// slot and today's page. After the app starts, React renders the same string
// into the slot (usePaidLanding decides it from the URL), so hydration finds
// what the script built.
//
// The rules are inline here, not in index.css, because the prerender inlines
// only the critical rules its markup already matches, and these match only
// once the attribute is set and the block is built.

export const SLOT_ID = 'ad-landing-slot';
export const DATA_ID = 'ad-landing-data';

/** The answers the page can give, as the JSON the slot is built from:
 * {<key>: {q, a, src: [{key, label, url, where, read}]}}. */
export function adLandingData(college, landings) {
    const out = {};
    for (const l of landings) {
        const seen = new Set();
        const src = [];
        for (const f of l.facts) {
            const id = `${f.source}|${f.where || ''}`;
            const s = college.sources[f.source];
            if (seen.has(id) || !s) continue;
            seen.add(id);
            src.push({ key: f.source, label: s.label, url: s.url, where: f.where || '', read: formatCalendarDate(s.read) });
        }
        out[l.key] = { q: l.question, a: `${l.facts.map((f) => f.text).join(' ')} ${l.close}`, src };
    }
    return out;
}

// Serialized into the page as the slot's builder, so it must stand alone:
// no imports, no outer names, ES5 only (the in-app browsers run it).
export function adLandingHtml(data, term) {
    // own keys only: "constructor" passes the term pattern and is on every object
    var own = !!data && Object.prototype.hasOwnProperty.call(data, term);
    var a = data && (own ? data[term] : data.generic);
    if (!a) return '';
    var esc = function (s) {
        return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    };
    var key = own ? term : 'generic';
    var html = '<div class="adl" data-ad-for="' + esc(key) + '" data-testid="ad-landing-' + esc(key) + '">'
        + '<h2 class="adl-q">' + esc(a.q) + '</h2><p class="adl-a">' + esc(a.a) + '</p>';
    for (var i = 0; i < a.src.length; i++) {
        var s = a.src[i];
        html += '<p class="adl-src">Source: <a class="adl-link" href="' + esc(s.url) + '" target="_blank" rel="noopener noreferrer" data-source="' + esc(s.key) + '">'
            + esc(s.label)
            + '<svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"></path></svg>'
            + '<span class="sr-only"> (opens in a new tab)</span></a>'
            + (s.where ? ', ' + esc(s.where) : '') + '. Read ' + esc(s.read) + '.</p>';
    }
    return html + '</div>';
}

// The script after the slot: fills it on a paid visit, while parsing.
export function mountScript() {
    return `(function(build){try{var t=document.documentElement.getAttribute(${JSON.stringify(AD_LANDING_ATTR)});if(t===null)return;`
        + `var d=document.getElementById(${JSON.stringify(DATA_ID)}),s=document.getElementById(${JSON.stringify(SLOT_ID)});`
        + `if(d&&s&&!s.firstChild)s.innerHTML=build(JSON.parse(d.textContent),t);}catch(e){}})(${adLandingHtml.toString()});`;
}

const A = `html[${AD_LANDING_ATTR}]`;
const HEAD = `${A} .college-head`;

/** The page's CSS for its paid layout (no text, so nothing for a crawler). */
export const AD_LANDING_CSS = [
    '.ch-ad{display:none}',
    `${HEAD}{display:flex;flex-direction:column}`,
    `${HEAD}>.ch-back,${HEAD}>.ch-answer{display:none}`,
    `${HEAD}>.ch-name{order:1;margin-top:0;gap:.75rem}`,
    `${HEAD}>.ch-name h1{font-size:1.25rem;line-height:1.2}`,
    `${HEAD}>.ch-name img,${HEAD}>.ch-name>div[aria-hidden]{width:40px!important;height:40px!important}`,
    `${HEAD}>.ch-ad{display:block;order:2;margin-top:1rem}`,
    `${HEAD}>.ch-stand{order:3}`,
    `${HEAD}>.ch-tiles{order:4}`,
    `${HEAD}>.ch-photo{order:5}`,
    `${HEAD}>.ch-intro{order:6}`,
    `${HEAD}>.ch-ask{order:7}`,
    // the block's own type, as the Tailwind classes it was first drawn with
    ".adl-q{margin:0;font-family:'Playfair Display',Georgia,serif;font-size:1.6rem;font-weight:700;line-height:1.25;color:#1A2E1F}",
    '.adl-a{margin:.5rem 0 0;font-size:1rem;line-height:1.625;color:#1A2E1F}',
    '.adl-src{margin:.25rem 0 0;font-size:.75rem;line-height:1rem;color:#6B6B6B}',
    '.adl-link{display:inline-flex;align-items:center;gap:.125rem;font-weight:500;color:#1A4D2E;text-decoration:underline;text-underline-offset:2px}',
    '.adl-link svg{width:.75rem;height:.75rem}',
    `@media (min-width:640px){${HEAD}>.ch-name h1{font-size:1.5rem}.adl-q{font-size:1.875rem;line-height:2.25rem}.adl-a{font-size:1.125rem;line-height:1.75rem}}`,
    // A phone held sideways (22 of 99 YouTube phones, Oct 13-16) has a
    // screen about 390 px tall: the question and the card sit side by side.
    `@media (orientation:landscape) and (max-height:500px){${HEAD}{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);column-gap:1.25rem;align-items:start}`
    + `${HEAD}>*{grid-column:1/-1}${HEAD}>.ch-ad{grid-column:1;grid-row:2}${HEAD}>.ch-stand{grid-column:2;grid-row:2}}`,
].join('');

/** `landings`: landingsFor(c). `landing`: usePaidLanding's, or null. */
export default function AdLandingBlock({ college, landings, landing }) {
    const slot = useRef(null);
    // Source links in the built block count like the page's own (SourceNote).
    useEffect(() => {
        const el = slot.current;
        if (!el) return undefined;
        const onClick = (e) => {
            const a = e.target && e.target.closest ? e.target.closest('a.adl-link') : null;
            if (a) track(EVENTS.SOURCE_LINK_CLICKED, { source: `college_${a.getAttribute('data-source')}`.slice(0, 40), surface: 'college-page' });
        };
        el.addEventListener('click', onClick);
        return () => el.removeEventListener('click', onClick);
    }, [landings.length]);
    if (!landings.length) return null;
    const data = adLandingData(college, landings);
    const html = landing ? adLandingHtml(data, landing.term) : '';
    // "<" escaped so a value can never close the data tag
    const json = JSON.stringify(data).replace(/</g, '\\u003c');
    return (
        <>
            <style>{AD_LANDING_CSS}</style>
            <script type="application/json" id={DATA_ID} dangerouslySetInnerHTML={{ __html: json }} />
            <div id={SLOT_ID} ref={slot} className="ch-ad" data-testid="ad-landing" dangerouslySetInnerHTML={{ __html: html }} />
            <script dangerouslySetInnerHTML={{ __html: mountScript() }} />
        </>
    );
}
